import { useMemo } from "react";
import { AlertTriangle, Users, ClipboardCheck } from "lucide-react";
import {
  buildDeploymentStabilityIndex,
  buildDsiGaugeStyle,
  buildDeploymentHeroCopy,
  buildDeploymentHeroKpis,
  buildTenureBars,
  buildRiskComposition,
  buildQualityCards,
  buildRiskSegments,
  buildDeploymentActions,
} from "@/utils/ctteacherDeploymentDerived";
import { toTitleCase } from "../utils/toTitleCase";

const ACTION_ICONS = {
  red: AlertTriangle,
  green: Users,
  orange: ClipboardCheck,
};

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const CTTeacherDeploymentTab = ({
  tenureData = null,
  ageData = null,
  dataQuality = null,
}) => {
  const dsi = useMemo(
    () => buildDeploymentStabilityIndex(tenureData, ageData),
    [tenureData, ageData]
  );
  const gaugeStyle = useMemo(() => buildDsiGaugeStyle(dsi), [dsi]);
  const heroCopy = useMemo(
    () => buildDeploymentHeroCopy(tenureData, ageData),
    [tenureData, ageData]
  );
  const heroKpis = useMemo(
    () => buildDeploymentHeroKpis(tenureData, ageData),
    [tenureData, ageData]
  );
  const tenureBars = useMemo(() => buildTenureBars(tenureData), [tenureData]);
  const riskComposition = useMemo(
    () => buildRiskComposition(tenureData, ageData),
    [tenureData, ageData]
  );
  const qualityCards = useMemo(() => buildQualityCards(dataQuality), [dataQuality]);
  const riskSegments = useMemo(
    () => buildRiskSegments(tenureData, ageData, dataQuality),
    [tenureData, ageData, dataQuality]
  );
  const actions = useMemo(() => buildDeploymentActions(tenureData), [tenureData]);

  return (
    <div className="ctdr-deployment-tab" data-testid="ctteacher-deployment">
      <section className="ctdr-hero-card" id="ctdr-hero">
        <div className="ctdr-hero-layout">
          <div>
            <div className="ctdr-health-main">
              <div
                className="ctdr-gauge"
                style={gaugeStyle}
                aria-label={`Deployment stability index ${dsi} percent`}
              >
                <div>
                  <strong>{dsi}</strong>
                  <span>DSI</span>
                </div>
              </div>
              <div className="ctdr-health-copy">
                <h2>{toTitleCase(heroCopy.headline)}</h2>
                <p>{heroCopy.narrative}</p>
                <div className="ctdr-status-row">
                  {heroCopy.chips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`ctdr-status-chip ${chip.className}`.trim()}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="ctdr-summary-note">
              <div className="ctdr-icon-box orange">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{heroCopy.primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="ctdr-metric-grid" aria-label="Deployment and risk KPIs">
            {heroKpis.map((card) => (
              <article
                key={card.label}
                className={`ctdr-metric-card ${card.cardClass}`.trim()}
              >
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="ctdr-section-title" id="ctdr-tenure">
        <div>
          <h2>Service Tenure Distribution</h2>
          <p>
            The existing single chart is expanded into a decision view: stable cohorts,
            transition cohorts and risk cohorts are visually separated.
          </p>
        </div>
        <button
          type="button"
          className="ctdr-section-action"
          onClick={() => scrollToId("ctdr-quality")}
        >
          Check data quality →
        </button>
      </div>

      <section className="ctdr-analytics-grid">
        <article className="ctdr-panel">
          <div className="ctdr-panel-head">
            <div>
              <h3>Tenure Pipeline by Service Band</h3>
              <p>
                Green bands show early service capacity, amber bands need deployment
                balancing, and red bands require succession planning.
              </p>
            </div>
            <span className="ctdr-pill amber">
              {tenureBars.totalRecords.toLocaleString("en-IN")} records
            </span>
          </div>
          <div className="ctdr-tenure-chart" aria-label="Service tenure distribution bar chart">
            <div className="ctdr-axis">
              {tenureBars.axisTicks.map((tick) => (
                <span key={tick}>{tick}</span>
              ))}
            </div>
            <div className="ctdr-bars">
              {tenureBars.bars.map((bar) => (
                <div key={bar.band} className="ctdr-bar-col">
                  <span className="ctdr-bar-value">{bar.display}</span>
                  <div
                    className={`ctdr-vbar ${bar.barClass}`.trim()}
                    style={{ height: `${bar.heightPct}%` }}
                  />
                  <span className="ctdr-bar-label">{bar.band}</span>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className="ctdr-panel">
          <div className="ctdr-panel-head">
            <div>
              <h3>Risk Composition</h3>
              <p>Risk is presented as operational cohorts rather than only raw values.</p>
            </div>
            <span className="ctdr-pill red">Watchlist</span>
          </div>
          <div className="ctdr-risk-stack">
            {riskComposition.map((item) => (
              <div key={item.title} className="ctdr-risk-card">
                <div className="ctdr-risk-card-head">
                  <h4>{item.title}</h4>
                  <strong>{item.pct}%</strong>
                </div>
                <p>{item.body}</p>
                <div className="ctdr-progress-line">
                  <i className={item.barClass} style={{ width: `${item.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="ctdr-section-title" id="ctdr-quality">
        <div>
          <h2>Data Quality & Compliance Cockpit</h2>
          <p>Compliance metrics are converted into compact status cards with clear risk color coding.</p>
        </div>
        <button
          type="button"
          className="ctdr-section-action"
          onClick={() => scrollToId("ctdr-actions")}
        >
          View actions →
        </button>
      </div>

      <section className="ctdr-quality-grid" aria-label="Data quality and compliance metrics">
        {qualityCards.map((card) => (
          <article key={card.label} className={`ctdr-quality-card ${card.cardClass}`.trim()}>
            <small>{card.label}</small>
            <strong style={{ color: card.valueColor }}>{card.value}</strong>
            <span>{card.hint}</span>
          </article>
        ))}
      </section>

      <div className="ctdr-section-title" id="ctdr-actions">
        <div>
          <h2>Risk Segmentation and Action Queue</h2>
          <p>
            The bottom section turns the risk data into specific actions for HR, block
            officers and data teams.
          </p>
        </div>
        <button
          type="button"
          className="ctdr-section-action"
          onClick={() => scrollToId("ctdr-hero")}
        >
          Back to top ↑
        </button>
      </div>

      <section className="ctdr-two-column">
        <article className="ctdr-panel ctdr-table-card">
          <div className="ctdr-table-head">
            <div>
              <h3>Teacher Risk Segment Table</h3>
              <p>Prioritized cohorts for district-level review.</p>
            </div>
            <span className="ctdr-pill">Segments</span>
          </div>
          <table aria-label="Teacher deployment risk segments">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Cohort</th>
                <th>Signal</th>
                <th>Risk Level</th>
                <th>Primary Owner</th>
                <th>Recommended Action</th>
              </tr>
            </thead>
            <tbody>
              {riskSegments.map((row) => (
                <tr key={row.priority}>
                  <td>{row.priority}</td>
                  <td>
                    <strong>{row.cohort}</strong>
                  </td>
                  <td>{row.signal}</td>
                  <td>
                    <span className={`ctdr-tag ${row.riskClass}`.trim()}>{row.riskLevel}</span>
                  </td>
                  <td>{row.owner}</td>
                  <td>{row.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="ctdr-panel">
          <div className="ctdr-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Focused next steps for the Deployment & Risk tab.</p>
            </div>
            <span className="ctdr-pill amber">Priority</span>
          </div>
          <div className="ctdr-action-card">
            {actions.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? AlertTriangle;
              return (
                <div key={item.title} className="ctdr-action-item">
                  <div className={`ctdr-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
            <div className="ctdr-action-footer">
              <b>Suggested review cadence</b>
              <span>
                Review deployment risk fortnightly and data quality weekly until
                retirement-risk mapping and identity corrections are complete.
              </span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default CTTeacherDeploymentTab;
